import styles from './NCAudioChartsNext.module.css'
import styleUtils from 'shared-components/styles/util.module.css'
import { DSAudioChartProductNode, DSNCAudioChartsNext } from 'shared-definitions/types'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import ChartSelect from '../ui/ChartSelect'
import { useAbortableEffect } from '../hooks/use-abortable-effect'
import ChartOriginHints from './ChartOriginHints'
import { CHART_EMBED_LEAD_MARGIN } from 'shared-code/chart-embed'

interface ComparisonFilter {
  products: ProductName[]
  productMeasurementSets: DSAudioChartProductNode[] | null
}

interface NCNcaudioChartsNextProps {
  data: DSNCAudioChartsNext
}

// temporary hide the controls
const showSelects = false

const NCAudioChartsNext: React.FC<NCNcaudioChartsNextProps> = ({ data }) => {
  const [chartUrl, setChartUrl] = useState<URL | null>(null)
  const { ref, inView } = useInView({ triggerOnce: true, rootMargin: CHART_EMBED_LEAD_MARGIN })

  // dynamic fields
  const [comparisonFilterData, setComparisonFilterData] = useState<ComparisonFilter | null>(null)
  const [selectedComparison, setSelectedComparison] = useState<DSAudioChartProductNode | null>(
    data.comparison
  )
  const [filterValue, setFilterValue] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [measureDropdownOpen, setMeasureDropdownOpen] = useState(false)

  const handleProductChange = (product: ProductName): void => {
    setSelectedComparison({
      productId: product.id,
      measurementSet: 'default',
    })
    setDropdownOpen(false)
    setFilterValue('')
  }

  const handleProductMeasurementSetChange = (measure: DSAudioChartProductNode): void => {
    setSelectedComparison(measure)
    setMeasureDropdownOpen(false)
    setFilterValue('')
  }

  const filteredProducts =
    comparisonFilterData?.products.filter(cursor =>
      cursor.name.toLowerCase().includes(filterValue.toLowerCase())
    ) ?? []

  const filteredMeasurementSets =
    comparisonFilterData?.productMeasurementSets?.filter(cursor =>
      cursor.measurementSet.toLowerCase().includes(filterValue.toLowerCase())
    ) ?? []

  const selectedProduct = comparisonFilterData?.products.find(
    cursor => cursor.id === selectedComparison?.productId
  )

  useEffect(() => {
    if (!inView) {
      return
    }

    // show the chart
    setChartUrl(buildUrl(`${data.urls.preview}chart`, { ...data, comparison: selectedComparison }))
  }, [inView, selectedComparison])

  useAbortableEffect(
    async abortSignal => {
      if (!data.comparison) {
        return
      }

      if (data.comparison.useMeasurementsFilter) {
        const [products, productMeasurementSets] = await Promise.all([
          getAllProductNames(data.urls.api, abortSignal),
          getMeasurementSets(data.urls.api, data.current.productId, abortSignal),
        ])

        if (data.comparison.allowedProductIds) {
          // filter by ids
          setComparisonFilterData({
            products: products.filter(
              cursor => data.comparison?.allowedProductIds?.includes(cursor.id) ?? true
            ),
            productMeasurementSets,
          })
        } else {
          setComparisonFilterData({ products, productMeasurementSets })
        }
      } else {
        setComparisonFilterData({
          products: await getAllProductNames(data.urls.api, abortSignal),
          productMeasurementSets: null,
        })
      }
    },
    [data.comparison, data.current.productId]
  )

  return (
    <div className={clsx(styleUtils.nc, styles.container)} ref={ref}>
      <ChartOriginHints url={data.urls.preview} />
      <div className={styles.audioChartPlaceholder} />
      {/* Deliberately NOT hidden until the iframe's `load` event. That event waits on every
          subresource the embedded app pulls — ~280KB of JS/CSS plus ~146KB of fonts — while the
          curve itself paints as soon as that app hydrates. Gating on `load` was holding a fully
          drawn chart behind an opaque box for the length of a font download. The placeholder
          sits behind the iframe instead, so the reader sees progress rather than nothing. */}
      <iframe
        className={styles.audioChart}
        title="Audio Chart"
        src={chartUrl?.toString() ?? undefined}
      />
      {showSelects && comparisonFilterData && selectedComparison && selectedProduct && (
        <ChartSelect
          label="Comparison model"
          selectedItemName={selectedProduct.name}
          items={filteredProducts}
          onItemClick={handleProductChange}
          isItemSelected={item => selectedProduct.id === item.id}
          getItemName={item => item.name}
          filterValue={filterValue}
          onFilterChange={e => setFilterValue(e.target.value)}
          onResetFilter={() => setFilterValue('')}
          isOpen={dropdownOpen}
          toggleOpen={setDropdownOpen}
          emptyMessage="No matching model"
        />
      )}
      {showSelects && comparisonFilterData?.productMeasurementSets && selectedComparison && (
        <ChartSelect
          label="Listening mode"
          selectedItemName={selectedComparison.measurementSet}
          items={filteredMeasurementSets}
          onItemClick={handleProductMeasurementSetChange}
          isItemSelected={item => selectedComparison.measurementSet === item.measurementSet}
          getItemName={item => item.measurementSet}
          filterValue={filterValue}
          onFilterChange={e => setFilterValue(e.target.value)}
          onResetFilter={() => setFilterValue('')}
          isOpen={measureDropdownOpen}
          toggleOpen={setMeasureDropdownOpen}
          emptyMessage="No matching measure"
        />
      )}
    </div>
  )
}

export default NCAudioChartsNext

// Utilities

async function getMeasurementSets(
  _baseUrl: string,
  _productId: number,
  _abortSignal?: AbortSignal
): Promise<DSAudioChartProductNode[]> {
  // url: baseUrl + '/get_product_measurement_sets?id=3341'

  // dummy
  return Promise.resolve([
    {
      productId: 3341,
      measurementSet: 'WIRED Nominal',
    },
    {
      productId: 3341,
      measurementSet: 'WIRED Raised',
    },
  ])
}

interface ProductName {
  id: number
  name: string
}

async function getAllProductNames(
  _baseUrl: string,
  _abortSignal?: AbortSignal
): Promise<ProductName[]> {
  // url: baseUrl + '/get_all_product_names'

  // dummy
  return Promise.resolve([
    {
      id: 2045,
      name: 'Jaybird Vista 2',
    },
    {
      id: 3341,
      name: 'JBL Tune 500',
    },
    {
      id: 3211,
      name: 'Logitech G333',
    },
  ])
}

function buildUrl(
  baseUrl: string,
  opts: Pick<DSNCAudioChartsNext, 'chartType' | 'current' | 'targetId'> & {
    comparison: DSAudioChartProductNode | null
  }
): URL {
  // Example URLs:
  // /chart?chartType=fr&id=4542&measurementSet=default&targetId=1
  // /chart?chartType=isolation&id=4542&measurementSet=USB+WIRED+Nominal
  // /chart?chartType=error&id=4542&measurementSet=default&targetId=1
  // /chart?chartType=frcomp&id=4542&measurementSet=USB+WIRED+Nominal&comparisonId=5521&comparisonSet=APTX+ANC+OFF+NOMINAL
  // /chart?chartType=isocomp&id=4542&measurementSet=USB+WIRED+Nominal&comparisonId=5521&comparisonSet=APTX+ANC+OFF+NOMINAL
  // /chart?chartType=thd&id=4542&measurementSet=USB+WIRED+Nominal
  // /chart?chartType=crosstalk&id=4542&measurementSet=USB+WIRED+Nominal
  // /chart?chartType=channelbalance&id=4542&measurementSet=USB+WIRED+Nominal
  // /chart?chartType=frlinearity&id=4542&measurementSet=USB+WIRED+Nominal&comparisonId=4542&comparisonSet=3.5MM+ACTIVE+Nominal
  const url = new URL(baseUrl)
  url.searchParams.set('chartType', opts.chartType)
  url.searchParams.set('id', opts.current.productId.toString())
  url.searchParams.set('measurementSet', opts.current.measurementSet)

  if (opts.targetId) {
    url.searchParams.set('targetId', opts.targetId.toString())
  }
  if (opts.comparison) {
    url.searchParams.set('comparisonId', opts.comparison.productId.toString())
    url.searchParams.set('comparisonSet', opts.comparison.measurementSet)
  }

  return url
}
